import {
  Alert,
  APIActions,
  Box,
  Button,
  ContentTemplate,
  CustomDialog,
  Grid,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  Pill,
  PillVariant,
  SearchBar,
  SectionHeader,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TableState,
  Typography,
  useDataTable,
} from 'web-library';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { ApplicationStatus } from '../../../api/candidate/exam/model';
import RouteKey from '../../../routes/key';
import {
  SearchGroupAmendmentsResponse,
  usePostSearchGroupAmendments,
} from '../../../api-refactored/group-subject-amendment/usePostSearchGroupAmendments';
import { stringToLongDateAndShortTime } from '../../../utils/DateUtils';
import { candidateService } from '../../../api';
import SelectExamLevelModal from './SelectExamLevelModal';
import AmendmentRequestActionButtons from './AmendmentRequestActionButtons';

const { amendment } = RouteKey.child;
const { coordinatorExamRegistrations, coordinatorRegistration } = RouteKey.amendment.child;

const useStyles = makeStyles()((theme) =>
({
  examLevelCol: {
    '&:not(:last-child)': {
      marginBottom: theme.spacing(3),
    },
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  searchContainer: {
    marginBottom: theme.spacing(5),
  },
  searchInput: {
    maxWidth: '100%',
  },
  iconRemove: {
    paddingRight: theme.spacing(1),
    margin: 'auto',
  },
  button: {
    padding: 0,
    minWidth: 0,
    height: theme.spacing(3),
    whiteSpace: 'nowrap',
    paddingRight: theme.spacing(6),
    width: 'max-content',
  },
  buttonRemove: {
    paddingRight: 0,
    color: theme.color.state.stateError,
  },
  alert: {
    marginBottom: theme.spacing(3),
  },
}));

const useTableColumns = (
  classes: any,
  theme: any,
  tableState: TableState,
  handleOpenDeleteModal: (uuid: string) => void
): MUIDataTableColumnDef[] => {
  const { t } = useTranslation();

  return [
    {
      name: 'applicationId',
      label: t('amendment->>AmendmentRequestListing.ApplicationId'),
      options: {
        sort: false,
      },
    },
    {
      name: 'candidateId',
      label: t('amendment->>AmendmentRequestListing.UinFinPassport'),
      options: {
        sort: false,
      },
    },
    {
      name: 'candidateName',
      label: t('amendment->>AmendmentRequestListing.CandidateName'),
      options: {
        sort: false,
      },
    },
    {
      name: 'examLevels',
      label: t('amendment->>AmendmentRequestListing.ExamLevel'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: any) => (
          <Box>
            {tableState.data[dataIndex].examLevels.map((exam: string, examIndex: number) => (
              <Typography variant="body2" key={`key-${exam}`}>
                {exam}
                {examIndex < tableState.data[dataIndex].examLevels.length - 1 && <>,</>}
              </Typography>
            ))}
          </Box>
        ),
      },
    },
    {
      name: 'applicationDateTime',
      label: t('amendment->>AmendmentRequestListing.ApplicationDateTime'),
      options: {
        sort: true,
        customBodyRenderLite: (dataIndex: any) => {
          if (tableState.data[dataIndex].applicationDateTime) {
            return (
              <Typography variant="body2">
                {stringToLongDateAndShortTime(tableState.data[dataIndex].applicationDateTime)}
              </Typography>
            );
          }
          return <Typography variant="body2">-</Typography>;
        },
      },
    },
    {
      name: 'status',
      label: t('amendment->>AmendmentRequestListing.Status'),
      options: {
        sort: false,
        customBodyRender: (value: any) => {
          let variant: PillVariant;

          switch (value) {
            case ApplicationStatus.DRAFT:
            case ApplicationStatus.PENDING_PAYMENT:
              variant = 'info';
              break;

            case ApplicationStatus.REGISTERED:
              variant = 'positive';
              break;

            default:
              return null;
          }

          return <Pill variant={variant} label={value} />;
        },
      },
    },
    {
      name: 'actions',
      label: t('amendment->>AmendmentRequestListing.Actions'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: any) => (
          <AmendmentRequestActionButtons
            classes={classes}
            theme={theme}
            tableState={tableState}
            handleOpenDeleteModal={handleOpenDeleteModal}
            dataIndex={dataIndex}
          />
        ),
        setCellProps: () => ({ style: { width: '25%' } }),
      },
    },
  ];
};

const DatatableAndModal = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();

  const theme = useTheme();

  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState('');

  const {
    state: tableState,
    dispatch: tableDispatch,
    tOptions,
  } = useDataTable({
    ...TABLE_INITIAL_STATE,
    size: 10,
  });

  const handleProceed = (examLevel: string) =>
    history.push(
      `/${amendment}/${coordinatorExamRegistrations.route}/${coordinatorRegistration.route}/${examLevel}`
    );

  const { mutate } = usePostSearchGroupAmendments({
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });

  const deleteDraft = candidateService.exam.candidate.useDeleteExamRegistrationDraft(
    () => {
      mutate({
        q: tableState.search,
        pathParams: {
          order: tableState.order,
          sort: tableState.sortColumn,
          page: tableState.page,
          pageSize: tableState.size,
        },
      });
    },
    () => null,
    () => null
  );

  const handleOpenDeleteModal = (uuid: string) => {
    setDeleteData(uuid);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  const handleDelete = () => {
    if (deleteData) {
      deleteDraft.mutate(deleteData);
    }

    handleCloseDeleteModal();
  };

  const tableColumns = useTableColumns(classes, theme, tableState, handleOpenDeleteModal);

  useEffect(() => {
    mutate({
      pathParams: {
        pageSize: tableState.size,
        page: tableState.page,
        order: tableState.order,
        sort: tableState.sortColumn,
      },
      q: tableState.search,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tableState.search,
    tableState.order,
    tableState.sortColumn,
    tableState.page,
    tableState.size,
  ]);

  const customOptions = useMemo<MUIDataTableOptions>(
    () => ({
      ...tOptions,
      pagination: true,
      filter: false,
      sort: true,
    }),
    [tOptions]
  );

  const amendmentAlert = (amendmentsResponses: SearchGroupAmendmentsResponse[]) => {
    const unavailableExamLevels = getUnavailableExamLevels(amendmentsResponses);

    if (unavailableExamLevels.length) {
      return (
        <Alert
          className={classes.alert}
          variant="filled"
          severity="error"
          message={t('amendment->>AmendmentRequestListing.Unavailable', {
            examLevels: unavailableExamLevels,
          })}
        />
      );
    }
    // Don't display any error if there is no draft
    return <></>;
  };

  const getUnavailableExamLevels = (amendmentsResponses: SearchGroupAmendmentsResponse[]) => {
    const unavailableExamLevels: string[] = [];

    amendmentsResponses.forEach((amendmentItem) => {
      if (amendmentItem.status === ApplicationStatus.DRAFT && !amendmentItem.windowOpened) {
        amendmentItem.examLevels.forEach((examLevel) => {
          if (
            unavailableExamLevels.every(
              (unavailableExamLevel) => unavailableExamLevel !== examLevel
            )
          ) {
            unavailableExamLevels.push(examLevel);
          }
        });
      }
    });
    unavailableExamLevels.sort((a, b) => a.localeCompare(b));

    return unavailableExamLevels.join(', ');
  };

  return (
    <ContentTemplate transparent continuous type="full">
      <Grid className={classes.headerContainer}>
        <SectionHeader title={t('amendment->>AmendmentRequestListing.SectionHeader')} />
        <Button
          id="button-group-amendment-new-request"
          onClick={() => setOpenModal(true)}
          variant="contained"
          color="secondary"
        >
          {t('amendment->>AmendmentRequestListing.NewSubjectAmendment')}
        </Button>
      </Grid>
      <Box className={classes.searchContainer}>
        {amendmentAlert(tableState.data)}
        <SearchBar
          className={classes.searchInput}
          placeholder={t('amendment->>AmendmentRequestListing.SearchByCandidateInfo')}
          name="searchInput"
          ariaLabel="searchInput"
          defaultValue={tableState.search}
          onSearch={(searchStr: string) => {
            tableDispatch({
              type: 'UPDATE_SEARCH',
              payload: searchStr,
            });
          }}
        />
      </Box>
      <MUIDataTable
        state={tableState}
        dispatch={tableDispatch}
        columns={tableColumns}
        data={tableState.data}
        options={customOptions}
        rowsPerPageOptions={[10, 20, 25, 30, 50]}
        emptyDataLabel={t('amendment->>AmendmentRequestListing.EmptyList')}
      />
      {openModal && (
        <SelectExamLevelModal
          handleProceed={handleProceed}
          examRegistrationType="SUBJECT_AMENDMENT"
          modalState={openModal}
          onDialogClose={() => setOpenModal(false)}
        />
      )}

      <CustomDialog
        open={openDeleteModal}
        title={t('amendment->>AmendmentRequestListing.DeleteModalTitle')}
        message={t('amendment->>AmendmentRequestListing.DeleteMessage')}
        action={
          <APIActions
            onClose={handleCloseDeleteModal}
            apiCall={handleDelete}
            cancelString={t('amendment->>AmendmentRequestListing.Cancel')}
            apiString={t('amendment->>AmendmentRequestListing.Proceed')}
          />
        }
      />
    </ContentTemplate>
  );
};

export default DatatableAndModal;
